import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Question } from "@src/pages/student/data.type";
import { TournamentResType } from "@src/pages/student/tournament/data&type";

interface ResponseTestCase {
  input: string;
  expectedOutput: string | Record<string, string>;
  actualOutput: string;
  result: boolean;
  error: string;
  testCaseType: "EASY" | "MEDIUM" | "HARD";
  testCaseScope?: "VISIBLE" | "HIDDEN";
}

export interface TournamentSubmissionEntry {
  status: string;
  duration: number | string;
  penalty: number;
}

export interface TournamentSubmissionState {
  [id: string]: TournamentSubmissionEntry[];
}

export interface CodeResultResponse {
  testCases: ResponseTestCase[];
  compilationError: string;
  questionId: string;
  versionId: string;
  studentId: string;
  labels: string[];
}
export interface StudentAnswers {
  [key: string]: Record<string, string>;
}

export interface SubmissionResult {
  createdOn?: string;
  createdByName: string;
  createdByEmail: string;
  id: string;
  userCode: string;
  marks: number;
  questionSubmitDuration: number;
  testCases: ResponseTestCase[];
  studentId: string;
  tournamentId: string;
  questionId: string;
  versionID: string;
  syntaxContribution: number;
  namingConventionContribution: number;
  logicContribution: number;
  status: "PASS" | "FAIL" | string;
  totalScore: number;
  failedAttempts: number;
  totalAttempts: number;
  totalTestCase: number;
  passedTestCase: number;
  questionNo: number;
  result: boolean;
  compilationError: string;
  submitTime?: string;
  labels?: string[];
  questionProgramType: string;
  runTime?: number;
}

interface TournamentLastSubmission {
  [id: string]: SubmissionResult;
}

interface InitialState {
  tournamentData: TournamentResType | null;
  selectedQuestion?: Question | null;
  codeResult: CodeResultResponse | null;
  studentAnswers: StudentAnswers | null;
  currentTournament: any;
  tournamentSubmissions: TournamentSubmissionState;
  questionColors: string[];
  tournamentLastSubmissions: TournamentLastSubmission;
}

const initialState: InitialState = {
  tournamentData: null,
  selectedQuestion: null,
  codeResult: null,
  studentAnswers: {},
  currentTournament: {
    id: "",
    name: "",
  },
  tournamentSubmissions: {},
  questionColors: [],
  tournamentLastSubmissions: {},
};

const studentTournamentSlice = createSlice({
  name: "studentTournamentScreen",
  initialState,
  reducers: {
    setTournamentData: (state, action: PayloadAction<TournamentResType>) => {
      state.tournamentData = { ...action.payload };
      state.selectedQuestion = action.payload.questionResponseList[0];
    },

    setSelectedQuestion: (state, action: PayloadAction<Question>) => {
      const selectedQuestionIndex =
        state?.tournamentData?.questionResponseList.findIndex(
          (q) => q.id === action.payload?.id
        ) || 0;
      if (selectedQuestionIndex === -1) return;

      const selectedQuestion =
        state?.tournamentData?.questionResponseList[selectedQuestionIndex];

      selectedQuestion?.version?.testCases?.map((testCase) => ({
        ...testCase,
        disabled: true,
      }));

      state.selectedQuestion = selectedQuestion;
    },

    addTestCase: (state) => {
      const testCases = state.selectedQuestion?.version.testCases;
      if (Array.isArray(testCases) && testCases.length > 0) {
        testCases.push({ ...testCases[testCases.length - 1], disabled: false });
      }
    },
    changeTestCaseInput: (state, { payload }) => {
      const testCases = state.selectedQuestion?.version.testCases || [];
      testCases[payload.selectedTestCase].input[payload.selectedInput] =
        payload.value;
    },
    setCodeResult: (state, action) => {
      state.codeResult = action.payload;
    },

    setInitialStudentAnswers: (state, action) => {
      state.studentAnswers = { ...state.studentAnswers, ...action.payload };
    },
    setStudentAnswers: (state, action) => {
      state.studentAnswers = action.payload;
    },
    addTournamentSubmission: (
      state,
      action: PayloadAction<{
        id: string;
        status: string;
        duration: string;
        penalty?: number;
      }>
    ) => {
      const { id, status, duration, penalty = 0 } = action.payload;

      if (!state.tournamentSubmissions[id]) {
        state.tournamentSubmissions[id] = [];
      }

      state.tournamentSubmissions[id].unshift({ status, duration, penalty });
    },
    clearTournamentSubmissions: (state) => {
      state.tournamentSubmissions = {};
      state.studentAnswers = {};
      state.tournamentData = null;
      state.selectedQuestion = null;
      state.codeResult = null;
      state.currentTournament = { id: "", name: "" };
      state.questionColors = [];
      state.tournamentLastSubmissions = {};
    },
    setQuestionColors: (state, action: PayloadAction<string[]>) => {
      state.questionColors = action.payload;
      state.tournamentLastSubmissions = {};
    },
    addTournamentLastSubmissions: (state, action) => {
      if (!state.tournamentLastSubmissions) {
        state.tournamentLastSubmissions = {};
      }

      const { questionId } = action.payload;

      state.tournamentLastSubmissions[questionId] = action.payload;
    },
  },
});

export const {
  setTournamentData,
  setSelectedQuestion,
  addTestCase,
  changeTestCaseInput,
  setCodeResult,
  setStudentAnswers,
  setQuestionColors,
  setInitialStudentAnswers,
  addTournamentSubmission,
  clearTournamentSubmissions,
  addTournamentLastSubmissions,
} = studentTournamentSlice.actions;
export default studentTournamentSlice.reducer;
